import { ReactNode, useState } from 'react';

import { IconHamburgerMenu, IconTimesDefault } from '@iwoca/icons';

import { Activation } from '../activation/Activation';
import { RequireAuthenticatedSeller } from '../auth/Auth';
import { IsSellerBlocked } from '../auth/IsSellerBlocked';
import { SellerNavBar } from '../components/SellerNavBar/SellerNavBar';
import iwocaPayLogo from '../components/svg/iwocaPayLogo.svg';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useSellerHandle } from '../hooks/useSellerHandle.hook';
import { useSellerWhitelabelling } from '../hooks/useSellerWhiteLabelling.hook';
import { useTrackSellerDashboardPageView } from '../hooks/useTrackSellerDashboardPageView';
import { PayLinksNavigation } from '../Seller/PayLinks/PayLinksNavigation';
import { AutoOpenWhatsNew } from '../Seller/WhatsNew/AutoOpenWhatsNew';
import { WhatsNewModal } from '../Seller/WhatsNew/WhatsNewModal';

// Defined at module scope (not inline in SellerPageTemplate) so a template
// re-render — e.g. opening the What's New pop-out — keeps their identity and
// reconciles rather than remounting. Inline components would get fresh
// identities each render and remount the whole page, discarding in-progress
// form state.
const MobileHeader = ({
  isSideBarVisible,
  onToggleSideBar,
}: {
  isSideBarVisible: boolean;
  onToggleSideBar: () => void;
}) => (
  <div className="grid grid-cols-[min-content,1fr] p-l">
    <img src={iwocaPayLogo} className="h-[24px]" alt="iwocaPay logo" />
    <div className="justify-self-end">
      <button
        className="flex w-fit border-none bg-transparent text-s font-med"
        onClick={onToggleSideBar}
      >
        Menu
        {isSideBarVisible ? (
          <IconTimesDefault className="h-[20px] w-[20px] pl-s" />
        ) : (
          <IconHamburgerMenu className="h-[20px] w-[20px] pl-s" />
        )}
      </button>
    </div>
  </div>
);

const MainContent = ({ children }: { children: ReactNode }) => (
  <div className="mx-auto w-full max-w-[1440px] px-2xl m:px-m">{children}</div>
);

const NavigationBar = ({
  onHideSideBar,
  onOpenWhatsNew,
}: {
  onHideSideBar: () => void;
  onOpenWhatsNew: () => void;
}) => (
  <header className="border-r-[1px] border-r-secondary-90 bg-secondary-95">
    <SellerNavBar hideSideBar={onHideSideBar} onOpenWhatsNew={onOpenWhatsNew} />
  </header>
);

const SellerPageTemplate = ({ children }: { children: ReactNode }) => {
  const [isSideBarVisible, setIsSideBarVisible] = useState<boolean>(false);
  const [isWhatsNewVisible, setIsWhatsNewVisible] = useState<boolean>(false);
  const isMobile = useMediaQuery('(max-width:1024px)');

  const navigationBar = (
    <NavigationBar
      onHideSideBar={() => setIsSideBarVisible(false)}
      onOpenWhatsNew={() => setIsWhatsNewVisible(true)}
    />
  );
  const mainContent = <MainContent>{children}</MainContent>;

  return (
    <div>
      {/* Own component (renders null) so the auto-open queries re-render only
          it, not this template. */}
      <AutoOpenWhatsNew setIsOpen={setIsWhatsNewVisible} />
      {/* Rendered at template level (not inside the nav) so it survives the
          mobile sidebar unmounting when a nav item is clicked. */}
      <WhatsNewModal
        isOpen={isWhatsNewVisible}
        closeModal={() => setIsWhatsNewVisible(false)}
      />
      {isMobile && (
        <div className="grid grid-cols-1">
          <MobileHeader
            isSideBarVisible={isSideBarVisible}
            onToggleSideBar={() => setIsSideBarVisible(!isSideBarVisible)}
          />
          {isSideBarVisible ? navigationBar : mainContent}
        </div>
      )}
      {!isMobile && (
        <div className="grid grid-cols-[200px_1fr]">
          {navigationBar}
          {mainContent}
        </div>
      )}
    </div>
  );
};

export const SellerInnerPageTemplate = ({
  title,
  additionalContent,
  children,
}: {
  title?: string;
  additionalContent?: ReactNode;
  children: ReactNode;
}) => {
  return (
    <div className="mb-l">
      <div className="mt-l flex w-full items-center">
        {title && <h1 className="m-0 mb-xl mt-xl text-4xl">{title}</h1>}
        {additionalContent && <>{additionalContent}</>}
      </div>
      {children}
    </div>
  );
};

export const NonAuthenticatedSellerOuterTemplate = ({
  children,
}: {
  children: ReactNode;
}) => {
  return <SellerPageTemplate>{children}</SellerPageTemplate>;
};

export const AuthenticatedSellerOuterTemplate = ({
  children,
}: {
  children: ReactNode;
}) => {
  useTrackSellerDashboardPageView();

  return (
    <RequireAuthenticatedSeller>
      <Activation>
        <SellerPageTemplate>
          <IsSellerBlocked>{children}</IsSellerBlocked>
        </SellerPageTemplate>
      </Activation>
    </RequireAuthenticatedSeller>
  );
};

export const SellerPayLinksTemplate = ({
  children,
}: {
  children: ReactNode;
}) => {
  // White-label theme.
  const { sellerHandle } = useSellerHandle();
  useSellerWhitelabelling(sellerHandle ?? undefined);

  return (
    <>
      <PayLinksNavigation />
      {children}
    </>
  );
};
